import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import { ContextMenuService } from '@core/services/context-menu.service';
import {
  CaseBattleAnimationEnum,
  CaseBattlesSlotsVariants,
  ICaseBattle,
  ICaseBattleRound,
} from '@features/case-battles/interfaces/case-battles.interface';
import { PlayerRowComponent } from '../../../core/components/player-row/player-row.component';
import { SessionService } from '@core/services/session.service';
import { GameIconComponent } from '../../../core/components/game-icon/game-icon.component';
import { CaseBattlesService } from '../case-battles.service';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import NumberFlow from 'number-flow';
import { continuous } from 'number-flow/plugins';
import {
  fadeInAnimation,
  fadeInOutAnimation,
} from '@core/utils/animations.util';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { sleepWithId } from '@core/utils/sleep.util';
import { getUserDropsCostBySlot } from '../utils/utils';

@Component({
  selector: 'app-cb-battle-player-2',
  imports: [
    PlayerRowComponent,
    GameIconComponent,
    BalanceIconComponent,
    CostComponent,
  ],
  templateUrl: './cb-battle-player-2.component.html',
  styleUrl: './cb-battle-player-2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [fadeInOutAnimation, fadeInAnimation],
})
export class CbBattlePlayer2Component {
  public contextMenuService = inject(ContextMenuService);
  public sessionService = inject(SessionService);
  private caseBattlesService = inject(CaseBattlesService);
  private injector = inject(Injector);

  animation = input.required<keyof typeof CaseBattleAnimationEnum>();
  battle = input.required<ICaseBattle>();
  slot = input.required<number>();

  player = computed(() => {
    return (
      this.battle().participants.find((p) => p.slot === this.slot()) ?? null
    );
  });
  settings = computed(() => {
    return this.caseBattlesService.getBattleSettings(this.battle());
  });

  promiseId = signal<number | null>(null);
  numberFlow = viewChild<ElementRef<NumberFlow>>('numberFlow');

  dropsCost = signal<Map<number, number>>(new Map());

  isWinner = computed(() => {
    return this.battle()
      .winners.map((player) => player.slot)
      .includes(this.slot() as CaseBattlesSlotsVariants);
  });

  showResults = computed(() => {
    return (
      this.animation() === 'finished' || this.animation() === 'battle-results'
    );
  });

  isHost = computed(
    () => this.sessionService.profile()?.steamid === this.battle().host
  );

  valueUpdateEffect = effect(async () => {
    const el = this.numberFlow()?.nativeElement;
    if (el && typeof this.dropsCost()) {
      const sum = Array.from(this.dropsCost().values()).reduce(
        (acc, value) => acc + value,
        0
      );

      if (!el.format) {
        this.initNumberFlow();
      }

      el.update(sum);
    }
  });

  dropEffect = effect(async () => {
    if (this.animation() === 'round-results') {
      const { ROLLING_CLOSER_TIMEOUT } = this.settings();
      const { promise, timeoutId } = await sleepWithId(ROLLING_CLOSER_TIMEOUT);
      this.promiseId.set(timeoutId);
      await promise;
      this.promiseId.set(null);
      this.addLatestDropCost();
    }
  });

  numberFlowInitEffect = afterRenderEffect(() => {
    this.initNumberFlow();
    this.numberFlowInitEffect.destroy();
  });

  ngAfterViewInit(): void {
    if (this.battle().status === 'finished') {
      this.setDropsCosts(this.battle().rounds);
    }
    if (this.battle().status === 'running') {
      this.setDropsCosts(this.battle().rounds.slice(0, -1));
    }
  }

  private initNumberFlow() {
    const el = this.numberFlow()?.nativeElement;
    if (el) {
      el.plugins = [continuous];
      el.format = {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
      el.locales = 'en-US';
      el.transformTiming = {
        duration: 300,
        //easing: 'linear',
      };
      el.spinTiming = {
        duration: 300,
        //easing: 'linear',
      };
      el.opacityTiming = {
        duration: 300,
        //easing: 'linear',
      };
    }
  }

  public async handleJoinBattle() {
    await this.caseBattlesService.joinBattle({
      uid: this.battle().uid,
      slot: this.slot() as CaseBattlesSlotsVariants,
    });
  }

  public async handleCallBots() {
    await this.caseBattlesService.callBots(this.battle().uid);
  }

  public addLatestDropCost() {
    const rounds = this.battle().rounds;
    const lastRound = rounds[rounds.length - 1];
    const roundNumber = rounds.length;
    const cost = getUserDropsCostBySlot([lastRound], this.slot());

    this.dropsCost.update((prev) => {
      if (!prev.has(roundNumber)) {
        prev.set(roundNumber, cost);
      }
      return new Map(prev);
    });
  }

  public setDropsCosts(rounds: ICaseBattleRound[]) {
    const map = new Map<number, number>();
    rounds.forEach((round, i) => {
      const roundNumber = i + 1;
      if (!map.has(roundNumber)) {
        const cost = getUserDropsCostBySlot([round], this.slot());

        map.set(roundNumber, cost);
      }
    });
    this.dropsCost.set(map);
  }

  public resetDropCosts() {
    this.dropsCost.set(new Map());
  }
}
